import { useMemo, useState } from "react";
import { useCollection } from "@/hooks/useCollection";
import { PageHeader, StatusBadge, Modal, useToast } from "./components/admin-ui";
import { contactMessages, type ContactMessage } from "@/mocks/admin";

const departments = [
  "General Enquiries",
  "Newsroom",
  "Advertising",
  "Partnerships",
  "Technical Support",
  "Careers",
  "Programs",
];

export default function MessagesPage() {
  const { items, update, remove } = useCollection<ContactMessage>("ltv_messages_v1", contactMessages);
  const { show } = useToast();
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState<ContactMessage | null>(null);
  const [reply, setReply] = useState("");

  const filtered = useMemo(() => {
    if (!filter) return items;
    return items.filter((m) => m.department === filter);
  }, [items, filter]);

  const unread = items.filter((m) => !m.read).length;

  function openMessage(m: ContactMessage) {
    setSelected(m);
    setReply("");
    if (!m.read) update(m.id, { read: true });
  }

  function sendReply() {
    if (!selected) return;
    if (!reply.trim()) {
      show("Write a reply before sending.", "error");
      return;
    }
    show(`Reply to ${selected.name} queued (email service not configured).`);
    setReply("");
  }

  const selectedLive = items.find((m) => m.id === selected?.id) ?? null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Messages"
        subtitle={`Contact enquiries and form submissions · ${unread} unread`}
        actions={
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-3 py-2 bg-white border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
          >
            <option value="">All departments</option>
            {departments.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        }
      />

      {filtered.length === 0 ? (
        <div className="bg-white rounded-lg border border-background-200 p-10 text-center">
          <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-message-line text-xl" />
          </div>
          <p className="mt-3 text-sm text-foreground-600">No messages in this department.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-background-200 divide-y divide-background-50">
          {filtered.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => openMessage(m)}
              className="w-full flex items-start gap-4 px-4 py-4 text-left hover:bg-background-50"
            >
              <span
                className={`w-9 h-9 flex items-center justify-center rounded-full shrink-0 ${
                  m.read ? "bg-background-100 text-foreground-500" : "bg-primary-100 text-primary-700"
                }`}
              >
                <i className="ri-user-line text-base" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`text-sm font-semibold ${m.read ? "text-foreground-800" : "text-foreground-950"}`}>
                    {m.name}
                  </span>
                  <StatusBadge tone="neutral">{m.department}</StatusBadge>
                  {!m.read && <span className="w-2 h-2 rounded-full bg-primary-500" />}
                </div>
                <p className="text-sm text-foreground-800 mt-0.5 truncate">{m.subject}</p>
                <p className="text-xs text-foreground-500 mt-0.5">{m.time}</p>
              </div>
              <span className="text-xs text-foreground-400 whitespace-nowrap">{m.email}</span>
            </button>
          ))}
        </div>
      )}

      <Modal
        open={selectedLive !== null}
        onClose={() => setSelected(null)}
        title={selectedLive?.subject ?? "Message"}
        subtitle={selectedLive ? `${selectedLive.name} · ${selectedLive.email} · ${selectedLive.department}` : ""}
        footer={
          <>
            <button
              type="button"
              onClick={() => {
                if (selectedLive) {
                  remove(selectedLive.id);
                  setSelected(null);
                  show("Message archived.");
                }
              }}
              className="px-4 py-2 text-sm font-medium text-accent-600 border border-accent-200 rounded-md hover:bg-accent-50 whitespace-nowrap"
            >
              Archive
            </button>
            <button
              type="button"
              onClick={sendReply}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 whitespace-nowrap"
            >
              Send Reply
            </button>
          </>
        }
      >
        {selectedLive && (
          <div className="space-y-4">
            <p className="text-sm text-foreground-800 whitespace-pre-wrap">{selectedLive.message}</p>
            <div>
              <label className="block text-sm font-medium text-foreground-800 mb-1.5">Reply</label>
              <textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                rows={4}
                placeholder="Write a reply…"
                className="w-full px-3 py-2 bg-background-50 border border-background-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
              />
              <p className="mt-1 text-xs text-foreground-500">
                Replies are queued locally. Connect an email service to deliver them.
              </p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}